// core/data/policies/moderation.ts — Moderation, Council & Appeals.
//
// The binding description of how a decision actually gets made: the two tracks,
// the rotating teams, what a report does, the Under Review promise and its one
// exception, the vote, the sanctions that exist, the single appeal, and — said
// plainly — the administrative capability the operators keep.
//
// AUTHORING RULES for this file:
//   * `summary` may only SIMPLIFY its `body`. Never a promise, exception, or
//     permission the body does not already contain.
//   * Describe nothing that does not exist. No timelines, no SLAs, no
//     transparency reports, no automated detection, no de-amplification.
//   * No thresholds an organiser could tune against: no report counts, no
//     percentages, no team sizes.
//   * Anything the owner still has to supply is left as a visible [PLACEHOLDER]
//     so it cannot ship by accident.

import type { PolicyDocument } from '@/core/models/policy';

export const moderationPolicy: PolicyDocument = {
  id: 'moderation',
  title: 'Moderation, Council & Appeals',
  blurb: 'Who decides what comes down, what they are allowed to act on, and how you appeal.',
  version: '1.00.02',
  updated: '2026-09-29',
  status: 'draft',
  sections: [
    {
      id: 'what-this-covers',
      heading: 'What this document covers',
      summary: 'How a report becomes a decision, who makes it, and what to do if it is wrong.',
      body: [
        'This is the working manual for moderation on TruXSocial. It explains what can be acted on, who decides, how they decide, and what happens afterwards.',
        'Free speech is the default here. Nothing in this document changes that. Everything below describes the narrow set of cases where the platform steps in, and the limits on how it may do so.',
        'It is written to be read, not survived. If something here is unclear, that is a fault in the document, and we would rather hear about it than have you guess.',
      ],
    },

    {
      id: 'two-tracks',
      heading: 'Two tracks, and which one applies',
      summary:
        'Nearly everything goes to the Council and its five categories. A separate, narrow track exists only for material that is unlawful to host at all.',
      body: [
        'There are exactly two ways content or an account can be acted on here.',
        "The first is the Council track. The Council is a group of human moderators who read a case, argue about it, and vote. The Council can act on five things and nothing else: direct threats, terrorism, scams, fraud, and coordinated abuse. That list is the whole of the Council's authority.",
        'The second is the legal floor. It covers a short, named list of material that is unlawful to host anywhere, and it is not put to a vote. It is set out in full below.',
        'The routing rule matters more than either track. If the Council track could handle something, the Council handles it. A case that is arguable is by definition a Council case, and it goes to a team and a vote, even when that takes longer, and even when the answer is unpopular.',
      ],
      callout: {
        tone: 'warning',
        text: 'The legal floor is not a faster route to the same result. It is never used to shortcut a judgement call. If a case could be argued either way, it belongs to the Council.',
      },
    },

    {
      id: 'the-five',
      heading: 'The five things the Council can act on',
      summary:
        'Direct threats, terrorism, scams, fraud, coordinated abuse. Not opinions, and not offence.',
      body: [
        'Direct threats: threatening violence against real people.',
        'Terrorism: promoting or coordinating terrorist acts.',
        'Scams: schemes built to defraud people.',
        'Fraud: impersonation or identity theft for gain.',
        'Coordinated abuse: organised campaigns meant to cause real harm.',
        'That is the entire list. The Council has no authority over anything else. It cannot act on a political opinion, an ideology, a rude post, a joke that landed badly, satire, parody, or a hard argument. Disagreement is not a violation, and being offended is not a report.',
        'When you report a post or an account to the Council, these five are the only reasons available to pick. That is deliberate. A report form with a general "this is bad" option becomes a tool for silencing people, so there is not one. The legal floor has its own separate reason, and it is not a sixth Council category.',
      ],
    },

    {
      id: 'legal-floor',
      heading: 'The legal floor',
      summary:
        'A short, closed list of material that is unlawful to host. It is removed without a vote, every removal is recorded, and it cannot be stretched to cover ordinary speech.',
      body: [
        "Some material cannot lawfully be hosted by anyone, anywhere, whatever a platform's own rules say. A vote cannot make it lawful, so it is not voted on.",
        'The legal floor covers, and covers only: child sexual abuse material; intimate images published without the consent of the person in them; and content that a valid court order requires the platform to remove.',
        'That list is closed. It is not a category of things "like" these. Nothing joins it by interpretation, only by publishing a new version of this document, so that you can see it change.',
        "Legal-floor removals are not sent to a Council team, because there is nothing to deliberate. They are carried out by the platform's operators, and each one is written to the platform's permanent record of administrative action.",
        'The routing rule still governs. If the material is arguably one of the five Council categories, it is a Council case and it goes to a vote. A broad reading of the legal floor is a failure of this policy, not a use of it.',
        'What you can contest is identification: that this is not the material we took it to be, that it is the wrong account, or that an order does not say what we read it to say. What you cannot contest is the rule itself. There is no Council appeal on a legal-floor removal, because the Council has no authority to permit the material.',
        'To raise a mistaken identification, or to send a legal notice, write to legal@truxsocial.org.',
      ],
      callout: {
        tone: 'critical',
        text: 'Child sexual abuse material is removed when it is identified and the account is banned. There is no vote and no warning. The only thing that can be contested is whether the identification was correct.',
      },
    },

    {
      id: 'who-the-council-is',
      heading: 'Who the Council is',
      summary: 'Human moderators, led by an Administrator. Council decisions are made by people.',
      body: [
        'The Council of Moderators is a pool of people. An Administrator leads the Council and has the final word on moderation policy. A Vice President runs the day-to-day work of getting cases reviewed.',
        'No software removes a post on TruXSocial. Every Council decision is a human decision, and it is recorded under the name of the team that made it.',
        'Moderators are members of this platform like anyone else. They are not anonymous to the platform, and they are not permanent fixtures of any one case.',
      ],
    },

    {
      id: 'rotating-teams',
      heading: 'Rotating, randomly assembled teams',
      summary:
        'Each case goes to a small team assembled at random for that case, which is then dissolved. It is how the platform resists factions.',
      body: [
        'When a case is ready for review, moderators are drawn from the pool at random into a small temporary team with a neutral name: Team Alpha, Team Horizon, and so on.',
        'The team reviews its case, votes, and is dissolved. Everyone returns to the pool and is reshuffled for the next case.',
        'This is the most important structural choice in moderation here. Standing panels develop opinions about people. A fixed team learns who it likes. Rotation means no faction can form around a viewpoint, no clique can quietly own a topic, and fresh eyes look at every case.',
        'Teams are named, not hidden. The decision on your post says which team made it.',
      ],
    },

    {
      id: 'reporting',
      heading: 'What a report does, and what it does not do',
      summary:
        'A single report removes nothing. Reports have to build up and broadly agree on the reason before a case opens.',
      body: [
        'Reporting a post does not hide it. It does not touch it at all. The post stays exactly where it was, fully visible, and the author is not notified of a single report.',
        'Reports against the same post collect over time. Two things must both be true before a case opens. Enough weight has to build up. Not every report counts the same. And the reports have to broadly agree on why: if people cite scattered, unrelated reasons, that is a crowd disagreeing, not a signal.',
        'We do not publish the exact numbers, and we are telling you why rather than pretending there are none. Publishing them would tell anyone organising a pile-on precisely how large to make it. What you can rely on is the shape of the rule: one report changes nothing, a mob of brand-new accounts does not outweigh a handful of long-standing ones, and reports that cannot agree on a reason do not open a case.',
        'Your trust score changes how much weight your reports carry, and nothing else. It never changes what you can post, what you can see, or how the Council judges your own posts. Trust is also not permanent: it reflects your standing and your track record, and it can fall as well as rise.',
        'Reporting is not a vote, and it is not a verdict. It asks people to look. That is all it does.',
      ],
    },

    {
      id: 'under-review',
      heading: 'The Under Review label',
      summary:
        'When a case opens, the post carries a visible label and stays readable while the Council decides. The legal floor is the one exception.',
      body: [
        'When a case opens, the post is labelled Under Review and the author is told. That label is the whole of what happens to it. The post is not hidden, not blurred, and not quietly shown to fewer people. Anyone who could read it before can read it while it is being judged.',
        'This is the point where most platforms remove first and decide later. TruXSocial does not, because "taken down while we look into it" is a removal: the post is gone for exactly the window in which anyone was going to read it.',
        'The single exception is the legal floor. Material on that short, closed list comes down when it is identified, before any review, because hosting it is not something the platform is permitted to weigh up. That exception exists so the promise above can be kept for everything else. Stretched, it would eat the promise, and this document would be worth nothing.',
      ],
      callout: {
        tone: 'warning',
        text: 'A post under review is a post nobody has judged yet. The label is not a verdict, and reading it as one is the mistake it exists to prevent.',
      },
    },

    {
      id: 'the-vote',
      heading: 'The vote',
      summary: 'The team votes. Without a clear majority to act, the content stays.',
      body: [
        'The team reads the post, the reports, and any context the author or the reporters gave. Then it votes on one question: does this break one of the five rules?',
        'It takes a clear majority of the team to act. Short of that, nothing happens and the content stays. A tie is not a violation. Where reasonable people are in genuine doubt, expression wins. That is not a loophole, that is the rule working as intended.',
        'The team votes on the content, not on the person. Popularity, politics, follower count, and whether someone pays for anything are not part of the question and are not evidence.',
      ],
    },

    {
      id: 'the-decision',
      heading: 'The written decision',
      summary:
        'Every case ends in a written explanation, given to the author and to the people who reported.',
      body: [
        'A case does not end in silence. When the team decides, the outcome is written down: which team decided, which of the five categories was considered, and what was done.',
        'The author is told. The people who reported are told. Where nothing was found, the explanation says why the content is protected, in plain terms rather than "no action taken".',
        'If a post is removed, a marker stays where it stood, so that a removal is visible as a removal rather than a post quietly vanishing.',
        "Resolved cases and their written explanations are listed in the Council's audit log, which any signed-in member can open.",
      ],
    },

    {
      id: 'sanctions',
      heading: 'What can actually happen',
      summary:
        'A post can be removed. An account can be suspended or banned. There is no warning tier.',
      body: [
        'The outcomes that exist are these. A post can be removed. An account can be suspended, which is temporary. An account can be banned, which is not.',
        'There is no warning level, and this document is not going to invent one so the list looks gentler. If nothing happened to your account, nothing happened to your account.',
        'Every suspension and every ban records the category it was issued under, when it was issued, and who issued it.',
        'Accounts can be reported and reviewed the same way posts are. That is for a pattern of behaviour, not for one post somebody disliked.',
      ],
    },

    {
      id: 'appeals',
      heading: 'Appeals',
      summary: 'One appeal per decision, heard by a different team, and that answer is final.',
      body: [
        'If the Council decides against you, you can appeal once. The appeal is on the decision notice attached to your post or your profile.',
        'Say why you believe the decision was wrong. That explanation is the first thing the second team reads.',
        'A fresh team hears the appeal, and every member of the original team is excluded. Nobody reviews their own decision.',
        'The second team can uphold the decision or overturn it. If it is overturned, your content and your standing are restored.',
        'That decision is final. There is no second appeal. This is a deliberate limit: it stops a case becoming a permanent argument, and it stops the process being worn down by whoever has the most time to spend on it.',
        'There is no deadline for filing an appeal. Nothing in the software refuses one because time has passed, and we are not going to publish a window we do not enforce. If a deadline is ever introduced, it will be named here before it applies to anyone.',
        "If your account is suspended or banned, you can still appeal without signing in. The sign-in screen shows why the account was sanctioned and takes your appeal there. That appeal is reviewed by the platform's operators rather than a Council team, because a signed-out account cannot open a case.",
      ],
    },

    {
      id: 'administrative-action',
      heading: 'What the operators can still do',
      summary:
        'The people who run TruXSocial keep a direct administrative capability for legal compulsion and platform integrity. Every use of it is recorded.',
      body: [
        'It would be a lie to tell you the Council is the only thing that can touch a post. It is not, and you should know that from us rather than work it out later.',
        "The platform's operators keep a direct capability to remove content and to suspend or ban accounts. It exists for two reasons and no others. The first is legal compulsion: an order the platform is required to obey. The second is platform integrity: attacks on the system itself, such as automated floods, where there is no judgement for a team to make.",
        'Every use of it writes a permanent entry: who acted, what they acted on, under which category, when, and any note they left. That record is append-only: the application never edits or deletes an entry.',
        'Using this capability for anything else (to settle an argument, to remove a post the Council would have kept) is not a grey area. It is a failure. It should also be visible as one: the post carries its removal marker, the account carries its status and the category it was given, and the action sits in the record.',
        'What does not exist is an independent body that reviews the operators. There is no outside auditor, no ombudsman, and no external appeals panel. Saying otherwise would be the easiest sentence in this document to write and the least honest one.',
      ],
      callout: {
        tone: 'warning',
        text: 'This capability sits outside the Council and outside the vote. It is bounded by what it is for, not by who is watching. Read the limits above as the promise they are, and hold us to them.',
      },
    },

    {
      id: 'what-does-not-happen',
      heading: 'Things this platform does not do',
      summary: 'No post is removed by software, and moderation here has no hidden states.',
      body: [
        'No post is removed by software. Every removal has a person behind it and a written reason attached.',
        'Moderation here has no quiet setting. A case has states you can see on the post (labelled, decided, removed) and no invisible ones.',
        'Being reported is not a judgement. It is not treated as evidence of anything on its own, and a report that never became a case leaves nothing behind on your post.',
        'The five rules apply the same way to the largest account here and to an account that opened this morning. Reach is neither a defence nor a charge.',
      ],
    },

    {
      id: 'if-we-got-it-wrong',
      heading: 'If we got it wrong',
      summary:
        'Report from the post or profile, appeal from the decision notice, and use the Feedback board to argue with the process itself.',
      body: [
        'To report a post or an account, use the report option on that post or profile. That is the route that opens a case.',
        'To contest a decision about you, use the appeal on the decision notice.',
        'To argue with this process rather than with one decision, use the Feedback board at /feedback. It is read by the people building the platform, and moderation feedback is read by the Council.',
        'For a legal-floor mistake, or a legal notice, write to legal@truxsocial.org.',
      ],
      callout: {
        tone: 'info',
        text: 'Replying to a post, or messaging a moderator, does not file a report and does not reach anyone who can act.',
      },
    },

    {
      id: 'changes',
      heading: 'When this document changes',
      summary: 'Changes are published here with a new version and date.',
      body: [
        'This document is versioned. When the rules change, the version and the date at the top change with them, and the change is described in the release notes under Settings, in Update Logs.',
        'The five categories are not a list we plan to grow. If they ever change, that change gets announced plainly rather than slipped into a revision.',
      ],
    },
  ],
};
